import { useEffect, useState } from 'react'
import {
  fetchComparacaoRange,
  fetchDailyRange,
  fetchMargens,
  type Comparacao,
} from '../../api/bridge'
import { janelaComparacao, type Janela, type ModoComparacao } from './comparacao'

export type SerieDia = { data: string; valor: number; pedidos: number }

export type ComparacaoHome = {
  /** Janela com que o período está sendo comparado. null enquanto não dá para calcular. */
  janela: Janela | null
  vendas: number | null
  pedidos: number | null
  /** Margem de contribuição da janela comparada — vem de `/margens/periodo`,
   *  porque `/vendas/comparacao` só devolve vendas e pedidos. */
  margem: number | null
  /** Receita dos pedidos com margem na janela comparada — denominador da MC %,
   *  o mesmo critério usado no período atual. */
  receitaComMargem: number | null
  serie: SerieDia[]
  carregando: boolean
  /** Falhou buscar: a tela mostra o período sem comparação em vez de mentir um zero. */
  erro: boolean
}

const VAZIO: ComparacaoHome = {
  janela: null,
  vendas: null,
  pedidos: null,
  margem: null,
  receitaComMargem: null,
  serie: [],
  carregando: false,
  erro: false,
}

function lerComparacao(c: Comparacao | null): { vendas: number | null; pedidos: number | null } {
  const bloco = c?.comparacao?.comparacao
  const vendas = bloco?.totalVendasValidas
  const pedidos = bloco?.totalPedidosValidos
  return {
    vendas: vendas == null ? null : Number(vendas),
    pedidos: pedidos == null ? null : Number(pedidos),
  }
}

/**
 * Busca a janela de comparação escolhida no seletor "Comparado com:".
 *
 * Três chamadas independentes, em paralelo — cada uma pode falhar sozinha sem
 * derrubar as outras: a comparação de vendas/pedidos, a margem da janela e a
 * série diária que desenha a linha tracejada.
 */
export function useComparacaoHome(
  inicio: string,
  fim: string,
  modo: ModoComparacao,
  marketplace: string,
  /** Muda quando o usuário pede refresh — refaz a busca. */
  tick: number,
): ComparacaoHome {
  const [estado, setEstado] = useState<ComparacaoHome>(VAZIO)

  useEffect(() => {
    const janela = janelaComparacao(inicio, fim, modo)
    if (!janela) {
      setEstado(VAZIO)
      return
    }

    let cancelado = false
    setEstado((a) => ({ ...a, janela, carregando: true, erro: false }))

    const mp = marketplace || undefined
    const principal = { inicio, fim }

    Promise.all([
      fetchComparacaoRange(principal, janela, mp).catch(() => null),
      fetchMargens({ dataInicio: janela.inicio, dataFim: janela.fim }, mp).catch(() => null),
      fetchDailyRange(janela.inicio, janela.fim, mp).catch(() => [] as SerieDia[]),
    ]).then(([comp, margens, serie]) => {
      if (cancelado) return
      const { vendas, pedidos } = lerComparacao(comp)
      const margem =
        margens?.margem?.margemTotal == null ? null : Number(margens.margem.margemTotal)
      const receitaComMargem =
        margens?.margem?.totalValorPago == null ? null : Number(margens.margem.totalValorPago)
      setEstado({
        janela,
        vendas,
        pedidos,
        margem,
        receitaComMargem,
        serie,
        carregando: false,
        // Só é erro quando nada veio — uma parte faltando ainda é utilizável.
        erro: comp == null && margens == null && serie.length === 0,
      })
    })

    return () => {
      cancelado = true
    }
  }, [inicio, fim, modo, marketplace, tick])

  return estado
}
